import { FC, useMemo, useState } from 'react';

import { Button, Icon, Modal } from '@components/index';
import { allowedCSVSizeForGroupCreation } from '@constants/file';
import { priorityOptions } from '@features/lead-management/constants/lead-info';
import {
  useGetAgentsQuery,
  useGetCategoriesQuery,
  useGetStatusOptionsQuery,
  useGetTagsQuery,
  useLeadBulkCreateMutation
} from '@features/lead-management/queries/LeadManagementQuery';
import {
  LeadAssignee,
  LeadCategory,
  LeadPriority,
  LeadSource,
  LeadStatus,
  LeadTag
} from '@features/lead-management/types/common';
import {
  LeadDataMappingLists,
  LeadUploadRow
} from '@features/lead-management/types/leadBulkCreate';
import {
  getCities,
  getLeadSourceOptions
} from '@features/lead-management/utils/common';
import {
  prepareLeadUpload,
  toCreatePayload
} from '@features/lead-management/utils/leadBulkCreate';
import useNotify from '@hooks/notify';
import { parseXLSXFile } from '@utils/file';

import LeadDownloadTemplateSection from './components/LeadDownloadTemplateSection';
import LeadParsedGrid from './components/LeadParsedGrid';
import LeadUploadFileSection from './components/LeadUploadFileSection';

import { CreateLeadRequest } from '../../types/request';

interface LeadBulkUploadModalProps {
  isOpen: boolean;
  handleCancel: () => void;
  organizationId: string;
  onCreateSuccess: () => void;
}

const LeadBulkUploadModal: FC<LeadBulkUploadModalProps> = ({
  isOpen,
  handleCancel,
  organizationId,
  onCreateSuccess
}) => {
  const { createErrorAlert, createSuccessAlert, createWarningAlert } =
    useNotify();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fileUploadError, setFileUploadError] = useState<string>('');
  const [file, setFile] = useState<File>();
  const [rows, setRows] = useState<LeadUploadRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rowErrorsById, setRowErrorsById] = useState<Record<string, string[]>>(
    {}
  );

  const { data: statusResp } = useGetStatusOptionsQuery({ organizationId });
  const { data: categoriesResp } = useGetCategoriesQuery({ organizationId });
  const { data: agentsResp } = useGetAgentsQuery({ organizationId });
  const { data: tagsResp } = useGetTagsQuery({ organizationId });
  const [leadBulkCreate, { isLoading: isBulkCreating }] =
    useLeadBulkCreateMutation();

  const resetState = () => {
    setFile(undefined);
    setHeaders([]);
    setRows([]);
    setRowErrorsById({});
  };

  const leadSourceOptions = getLeadSourceOptions();

  const sources = useMemo(
    () => leadSourceOptions.map((o) => String(o.label)),
    [leadSourceOptions]
  );
  const priorities = useMemo(
    () => priorityOptions.map((p) => String(p.label)),
    []
  );
  const statuses = useMemo(
    () =>
      statusResp?.length > 0
        ? statusResp?.map((d: LeadStatus) => String(d.value || ''))
        : [],
    [statusResp]
  );
  const categories = useMemo(
    () =>
      categoriesResp?.length > 0
        ? categoriesResp?.map((c: LeadCategory) => String(c.value || ''))
        : [],
    [categoriesResp]
  );
  const assignees = useMemo(
    () =>
      agentsResp?.sales_agents && agentsResp.sales_agents.length > 0
        ? agentsResp.sales_agents.map((a: LeadAssignee) => String(a.name || ''))
        : [],
    [agentsResp]
  );
  const tags = useMemo(
    () =>
      tagsResp?.length > 0
        ? tagsResp?.map((t: LeadTag) => String(t.name || ''))
        : [],
    [tagsResp]
  );

  const cityList = getCities();

  const handleFileProcessing = async (selectedFile: File) => {
    setFileUploadError('');
    try {
      const lists = {
        statuses,
        sources,
        tags,
        categories,
        priorities,
        assignees,
        cities: cityList
      };
      const {
        headers: inferredHeaders,
        rowsWithId,
        rowErrorsById: newRowErrorsById
      } = await prepareLeadUpload(selectedFile, lists);

      setFile(selectedFile);
      setHeaders(inferredHeaders);
      setRows(rowsWithId);
      setRowErrorsById(newRowErrorsById);
    } catch (error) {
      setFileUploadError('Error parsing file');
    }

    setTimeout(() => {
      setIsLoading(false);
      createSuccessAlert('File uploaded Successfully');
    }, 500);
  };

  const checkFileValidityAndProcess = (selectedFile: File) => {
    if (
      selectedFile?.type !== 'text/csv' &&
      selectedFile.type !==
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      setFileUploadError('Please provide a csv or xlsx file to upload');
      return false;
    }

    // validate file size
    if (selectedFile.size > allowedCSVSizeForGroupCreation) {
      setFileUploadError('Upload failed. File size more than 2MB');
      return false;
    }

    try {
      setIsLoading(true);
      const reader = new FileReader();
      reader.onload = async () => {
        let rowsInFile = 0;
        if (selectedFile.type === 'text/csv') {
          const csvText = await selectedFile.text();
          // csv data includes headers
          rowsInFile = csvText.split('\n').filter((line) => line).length - 1;
        } else {
          const parsedData = await parseXLSXFile(selectedFile);
          rowsInFile = (parsedData.data || []).length;
        }

        if (rowsInFile > 100) {
          setIsLoading(false);
          setFileUploadError('Maximum no. of leads allowed would be 100');
        } else {
          await handleFileProcessing(selectedFile);
        }
      };
      // reading triggers onload
      reader.readAsArrayBuffer(selectedFile);
    } catch (error) {
      setFileUploadError('Error reading file');
      return false;
    }
    return true;
  };

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      checkFileValidityAndProcess(selectedFile);
    }
  };

  const handleCreateLeads = async () => {
    if (!rows.length) {
      createWarningAlert('Please upload a file with at least one lead');
      return;
    }
    try {
      const leadDataMappingLists: LeadDataMappingLists = {
        statusList: statusResp as LeadStatus[],
        sourceList: leadSourceOptions as LeadSource[],
        tagsList: tagsResp as LeadTag[],
        categoryList: categoriesResp as LeadCategory[],
        priorityList: priorityOptions as LeadPriority[],
        assigneeList: agentsResp?.sales_agents as LeadAssignee[]
      };
      // mapping the valid rows to create request payloads
      const payloads: CreateLeadRequest[] = rows
        .map((rowdata, index) => ({ rowdata, index }))
        .filter(({ index }) => !rowErrorsById[`row-${index}`]?.length)
        .map(({ rowdata }) => toCreatePayload(rowdata, leadDataMappingLists));

      // leadbulk create api call
      const res = await leadBulkCreate({ organizationId, body: payloads });

      if ('data' in res && Array.isArray(res.data)) {
        const hasErrors = res.data.some(
          (r: any) => r.errors && r.errors.length > 0
        );
        if (hasErrors) {
          createErrorAlert(
            'Error adding leads. Please check the file and try again.'
          );
          // TODO: Handle showing the errors in the file sheet display
        }
      } else if ('data' in res && res.data.status === 'success') {
        createSuccessAlert('Leads added successfully');
        onCreateSuccess();
        resetState();
        handleCancel();
      } else {
        createErrorAlert('Error adding leads');
      }
    } catch (err: any) {
      createErrorAlert(err?.data?.message || 'Error adding leads');
    }
  };

  const onCancelClick = () => {
    resetState();
    if (!file) {
      handleCancel();
    }
  };

  const handleCloseModal = () => {
    resetState();
    handleCancel();
  };

  // limit preview for sidebar-like list if needed
  const parsedRowsPreview = useMemo(() => rows.slice(0, 5), [rows]);

  const totalErrorRows = Object.values(rowErrorsById).filter(
    (errors) => errors.length > 0
  ).length;

  return (
    <Modal
      isOpen={isOpen}
      onCancel={handleCloseModal}
      isCloseIconRequired
      isClickOutsideRequired={!file}
    >
      <div className="w-225 min-w-125 py-6">
        <div className="mb-3 px-6 text-20 font-medium leading-[28px] tracking-normal text-[#1C1C1D]">
          {file?.name ? file.name : 'Upload CSV/Excel'}
        </div>
        <div className="mt-6 flex flex-col gap-y-2 border-y border-[#E4E4E4] px-6 py-3">
          {!file && (
            <div className="flex items-center justify-between">
              <div className="font-dm-sans text-sm font-normal not-italic leading-5 tracking-normal text-[#7E7E7E]">
                Upload a CSV to quickly import user data
              </div>
              <LeadDownloadTemplateSection
                isFileAvailable={!!file}
                tableHeaders={headers}
                tableRows={parsedRowsPreview as any[]}
                statuses={statuses}
                sources={sources}
                tags={tags}
                categories={categories}
                priorities={priorities}
                assignees={assignees}
                cities={cityList}
              />
            </div>
          )}

          {!file && (
            <LeadUploadFileSection
              file={file}
              checkFileValidityAndProcess={checkFileValidityAndProcess}
              isLoading={isLoading}
              onFileSelect={onFileSelect}
            />
          )}
          {!!rows.length && (
            <LeadParsedGrid
              headers={headers}
              rows={rows as any[]}
              rowErrorsById={rowErrorsById}
              totalErrorRows={totalErrorRows}
            />
          )}
        </div>
        <div className="mt-3 flex justify-between gap-x-4 px-6">
          <div
            className="flex items-center gap-x-2"
            style={{ opacity: totalErrorRows > 0 ? 1 : 0 }}
          >
            <Icon name="alert-circle" className="!size-6" stroke="#CF392E" />
            <div className="text-sm font-normal not-italic leading-none tracking-normal text-error">
              The rows marked in red will not be added to Lead list
            </div>
          </div>
          <div className="flex items-center gap-x-4">
            <Button
              variant="secondary"
              className="!w-20"
              handleClick={onCancelClick}
              disabled={isBulkCreating}
              label="Cancel"
            />
            <Button
              variant="primary"
              className="!w-28"
              handleClick={handleCreateLeads}
              disabled={!rows.length || !!fileUploadError || isBulkCreating}
              label={isBulkCreating ? 'Adding…' : 'Add'}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LeadBulkUploadModal;
